import { Diff } from "lucide-react";
import type { ReactNode } from "react";
import type { IVScoreResponse } from "../../types/market-data";
import { colorForScore } from "../../lib/fuse-palette";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

interface IVScoreForecastFusesProps {
  prediction: NonNullable<IVScoreResponse["prediction"]> | null | undefined;
  variant?: "detail" | "strip";
}

export function IVScoreForecastFuses({
  prediction,
  variant = "detail",
}: IVScoreForecastFusesProps) {
  if (!prediction) return <ForecastPending variant={variant} />;
  return variant === "strip" ? (
    <ForecastStrip prediction={prediction} />
  ) : (
    <ForecastDetail prediction={prediction} />
  );
}

function ForecastDetail({
  prediction,
}: {
  prediction: NonNullable<IVScoreResponse["prediction"]>;
}) {
  return (
    <div className="py-2" data-iv-score-forecast="large">
      <ForecastHeading source={prediction.source} />
      <div className="mt-1 flex items-center gap-3 text-[10px] text-[var(--fintheon-muted)]/58">
        <span>
          Projected <ForecastScore score={prediction.nextSessionScore} />
        </span>
        <span>
          Confidence <ForecastConfidence value={prediction.confidence} />
        </span>
      </div>
      {prediction.scenarios.map((scenario) => (
        <div
          key={scenario.label}
          className="mt-1 flex items-center gap-2 text-[9px]"
        >
          <span className="min-w-0 flex-1 truncate text-[var(--fintheon-muted)]/58">
            {scenario.label}
          </span>
          <span className="font-mono text-[var(--fintheon-muted)]/48">
            <FintheonNumericText
              text={(scenario.probability * 100).toFixed(0)}
              kind="percentage-change"
            />
            %
          </span>
          <span
            className="font-mono"
            style={{ color: colorForScore(scenario.projectedScore) }}
          >
            <FintheonNumericText
              kind="score"
              text={scenario.projectedScore.toFixed(1)}
            />
          </span>
        </div>
      ))}
    </div>
  );
}

function ForecastStrip({
  prediction,
}: {
  prediction: NonNullable<IVScoreResponse["prediction"]>;
}) {
  const leadScenario = prediction.scenarios[0] ?? null;
  return (
    <div data-iv-score-forecast="edgecaster-strip">
      <ForecastHeading source={prediction.source} />
      <dl className="mt-2 grid grid-cols-3 gap-2">
        <ForecastFuse label="Projected">
          <ForecastScore score={prediction.nextSessionScore} />
        </ForecastFuse>
        <ForecastFuse label="Confidence">
          <ForecastConfidence value={prediction.confidence} />
        </ForecastFuse>
        <ForecastFuse label={leadScenario?.label ?? "Scenario"}>
          {leadScenario ? (
            <span style={{ color: colorForScore(leadScenario.projectedScore) }}>
              <FintheonNumericText
                kind="score"
                text={leadScenario.projectedScore.toFixed(1)}
              />
            </span>
          ) : (
            "Pending"
          )}
        </ForecastFuse>
      </dl>
    </div>
  );
}

function ForecastPending({ variant }: { variant: "detail" | "strip" }) {
  return (
    <div
      className={variant === "detail" ? "py-2" : ""}
      data-iv-score-forecast="pending"
    >
      <ForecastHeading source={null} />
      <p className="mt-2 text-[10px] text-[var(--fintheon-muted)]/52">
        Forecast pending from the shared IV source.
      </p>
    </div>
  );
}

function ForecastHeading({
  source,
}: {
  source: NonNullable<IVScoreResponse["prediction"]>["source"] | null;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Diff className="h-3.5 w-3.5 text-[var(--fintheon-accent)]" />
      <h5 className="text-[10px] font-semibold text-[var(--fintheon-accent)]">
        Next Session Forecast
      </h5>
      <span className="ml-auto font-mono text-[8px] text-[var(--fintheon-muted)]/42">
        {source
          ? source === "agentDesk"
            ? "AgentDesk"
            : "Heuristic"
          : "IV source"}
      </span>
    </div>
  );
}

function ForecastFuse({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="truncate text-[8px] uppercase tracking-[0.1em] text-[var(--fintheon-muted)]/48">
        {label}
      </dt>
      <dd className="mt-1 truncate font-mono text-[12px] tabular-nums text-[var(--fintheon-text)]/82">
        {children}
      </dd>
    </div>
  );
}

function ForecastScore({ score }: { score: number }) {
  return (
    <strong style={{ color: colorForScore(score) }}>
      <FintheonNumericText kind="score" text={score.toFixed(1)} />
      /10
    </strong>
  );
}

function ForecastConfidence({ value }: { value: number }) {
  return (
    <strong>
      <FintheonNumericText
        text={(value * 100).toFixed(0)}
        kind="percentage-change"
      />
      %
    </strong>
  );
}
